/**
 * AuditLogPage — firm-wide activity log viewer in the Admin console.
 * Backed by GET /v1/activity (activity.view capability) with actor/action/
 * entity-type/date-range/free-text filters and cursor "load more" paging.
 */

import { useState } from 'react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import type { UserOutput } from '@1financial/shared';
import { getAccessToken, authenticatedFetch as fetch } from '../../lib/auth-context.js';
import { httpError } from '../../lib/http-error.js';
import { SearchableSelect } from '../../components/SearchableSelect.js';
import { TableSkeletonRows } from '../../components/Skeleton.js';
import { clsx } from 'clsx';
import { fmtDateTime } from '../../lib/date.js';
import { DateInput } from '../../components/DateInput.js';
import { useUserPreference } from '../../lib/user-preferences.js';

interface AuditRow {
  id: string;
  userId: string | null;
  actor: { displayName: string; email: string } | null;
  actorType: 'USER' | 'CLIENT' | 'SYSTEM';
  action: string;
  entityType: string;
  entityId: string | null;
  summary: string;
  createdAt: string;
}

interface AuditPage {
  items: AuditRow[];
  nextCursor: string | null;
}

const ACTION_COLOURS: Record<string, string> = {
  CREATE: 'pill-success',
  UPDATE: 'pill-info',
  DELETE: 'pill-danger',
  BULK_DELETE: 'pill-danger',
  ARCHIVE: 'pill-neutral',
  RESTORE: 'pill-teal',
  LOGIN: 'pill-indigo',
  LOGOUT: 'pill-indigo',
  EXPORT: 'pill-warn',
  IMPORT: 'pill-warn',
  REASSIGN: 'pill-purple',
};

function actionColour(action: string) {
  return ACTION_COLOURS[action] ?? 'pill-neutral';
}

export function AuditLogPage() {
  const [actorId, setActorId] = useUserPreference('audit.actorId', '');
  const [action, setAction] = useUserPreference('audit.action', '');
  const [entityType, setEntityType] = useUserPreference('audit.entityType', '');
  const [from, setFrom] = useUserPreference('audit.from', '');
  const [to, setTo] = useUserPreference('audit.to', '');
  const [q, setQ] = useState('');
  const [includeSystem, setIncludeSystem] = useUserPreference('audit.includeSystem', false);

  const actors = useQuery({
    queryKey: ['admin-audit-actors'],
    queryFn: async () => {
      const res = await fetch('/v1/users?limit=100', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: UserOutput[] } };
      return body.data.items;
    },
  });

  const actions = useQuery({
    queryKey: ['admin-audit-actions'],
    queryFn: async () => {
      const res = await fetch('/v1/activity/actions', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: string[] } };
      return body.data.items;
    },
  });

  const entityTypes = useQuery({
    queryKey: ['admin-audit-entity-types'],
    queryFn: async () => {
      const res = await fetch('/v1/activity/entity-types', {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: { items: string[] } };
      return body.data.items;
    },
  });

  const list = useInfiniteQuery({
    queryKey: ['admin-audit', { actorId, action, entityType, from, to, q, includeSystem }],
    queryFn: async ({ pageParam }) => {
      const p = new URLSearchParams();
      if (actorId) p.set('userId', actorId);
      if (action) p.set('action', action);
      if (entityType) p.set('entityType', entityType);
      if (from) p.set('from', new Date(from + 'T00:00:00Z').toISOString());
      if (to) p.set('to', new Date(to + 'T23:59:59Z').toISOString());
      if (q.trim()) p.set('q', q.trim());
      if (includeSystem) p.set('actorTypes', 'USER,CLIENT,SYSTEM');
      if (pageParam) p.set('cursor', pageParam);
      p.set('limit', '50');
      const res = await fetch(`/v1/activity?${p.toString()}`, {
        headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
      });
      if (!res.ok) throw await httpError(res);
      const body = await res.json() as { ok: true; data: AuditPage };
      return body.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const selectCls = 'bg-bg-panel border border-border rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-accent';

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-muted">
          {rows.length} row(s) loaded · human events by default — system nudges/alerts stay recorded
        </p>
        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm text-muted cursor-pointer whitespace-nowrap">
            <input
              type="checkbox"
              checked={includeSystem}
              onChange={(e) => setIncludeSystem(e.target.checked)}
              className="rounded border-border bg-bg-base text-accent focus:ring-accent"
            />
            Include system events
          </label>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search summaries…"
            className="bg-bg-panel border border-border rounded-lg px-3 py-1.5 text-sm text-white placeholder-muted focus:outline-none focus:border-accent w-full sm:w-64"
          />
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
        <SearchableSelect
          value={actorId}
          onChange={setActorId}
          placeholder="All actors"
          options={(actors.data ?? []).map((u) => ({ value: u.id, label: `${u.displayName} (${u.email})` }))}
          className={clsx(selectCls, 'w-full')}
          wrapperClassName="w-full sm:w-64"
        />
        <select value={action} onChange={(e) => setAction(e.target.value)} className={selectCls}>
          <option value="">All actions</option>
          {(actions.data ?? []).map((a) => (
            <option key={a} value={a}>{a}</option>
          ))}
        </select>
        <select value={entityType} onChange={(e) => setEntityType(e.target.value)} className={selectCls}>
          <option value="">All entity types</option>
          {(entityTypes.data ?? []).map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <DateInput
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          aria-label="From date"
          className={selectCls}
        />
        <DateInput
          value={to}
          onChange={(e) => setTo(e.target.value)}
          aria-label="To date"
          className={selectCls}
        />
      </div>

      <div className="bg-bg-panel border border-border rounded-xl overflow-hidden overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead>
            <tr className="text-left text-xs text-muted bg-bg-card border-b border-border">
              <th className="px-4 py-3 font-medium">Timestamp</th>
              <th className="px-4 py-3 font-medium">Actor</th>
              <th className="px-4 py-3 font-medium">Action</th>
              <th className="px-4 py-3 font-medium">Entity</th>
              <th className="px-4 py-3 font-medium">Summary</th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading && <TableSkeletonRows colSpan={5} />}
            {list.error && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-rose-400">{(list.error as Error).message}</td></tr>
            )}
            {!list.isLoading && !list.error && rows.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-muted">No audit rows match these filters.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-border/50 hover:bg-bg-hover/40">
                <td className="px-4 py-2.5 text-muted whitespace-nowrap">
                  {fmtDateTime(r.createdAt)}
                </td>
                <td className="px-4 py-2.5">
                  {r.actor ? (
                    <>
                      <span className="text-white">{r.actor.displayName}</span>
                      <span className="text-muted text-xs block">{r.actor.email}</span>
                    </>
                  ) : (
                    <span className="text-muted">{r.actorType === 'CLIENT' ? 'client' : 'system'}</span>
                  )}
                </td>
                <td className="px-4 py-2.5">
                  <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-md border text-xs font-medium', actionColour(r.action))}>
                    {r.action}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-muted">
                  {r.entityType}
                  {r.entityId && <span className="text-xs block font-mono">{r.entityId.slice(0, 8)}…</span>}
                </td>
                <td className="px-4 py-2.5 text-white">{r.summary}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {list.hasNextPage && (
        <div className="flex justify-center">
          <button
            onClick={() => void list.fetchNextPage()}
            disabled={list.isFetchingNextPage}
            className="text-sm text-muted hover:text-white border border-border hover:border-accent px-4 py-1.5 rounded-lg disabled:opacity-50"
          >
            {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  );
}
